<template>
  <div>
    <v-card>
      <v-card-title>Subordinates Leave Summary Report</v-card-title>
      <v-row class="px-4">
        <v-col>
          <v-autocomplete
            v-model="leaveType"
            label="Leave Type"
            :loading="loading"
            :items="leaveTypes"
            :error-messages="errors.collect('leaveType')"
            data-vv-name="leaveType"  
            data-vv-as="leave type"
            chips
            deletable-chips
            hide-selected
            multiple
            item-text="name"
            item-value="id"
            small-chips
          />
        </v-col>

        <v-col>
          <v-menu
            v-model="startDateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            :disabled="years.length < 1"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="startDateDisplay"
                v-validate="'required'"
                label="Start Date"
                prepend-icon="mdi-calendar"
                :error-messages="errors.collect('startDateDisplay')"
                data-vv-name="startDateDisplay"  
                data-vv-as="start date"    
                readonly
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="startDate"
              :min="minStartDate"
              :max="maxStartDate"
              @input="startDateChange"
            />
          </v-menu>
        </v-col>

        <v-col>
          <v-menu
            v-model="endDateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            :disabled="years.length < 1"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="endDateDisplay"
                v-validate="'required'"
                label="End Date"
                prepend-icon="mdi-calendar"
                :error-messages="errors.collect('endDateDisplay')"
                data-vv-name="endDateDisplay"  
                data-vv-as="end date"  
                readonly
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="endDate"
              :min="startDate"
              :max="maxEndDate"
              @input="endDateMenu = false"
            />
          </v-menu>
        </v-col>

        <v-col
          cols="2"
          class="ma-auto"
        >
          <v-row
            v-if="progressing" 
            class="text-center"
          >
            <v-col cols="12">
              <div>
                <span class="font-weight-bold">Downloading</span>
              </div>
              <v-progress-circular
                :rotate="360"
                :size="50"
                :width="5"
                :value="progressbar"
                color="teal"
              >
                {{ progressbar }}
              </v-progress-circular>
            </v-col>
          </v-row>

          <v-row class="text-center">
            <v-col cols="12">
              <v-btn
                class="mx-3"
                color="primary"
                :loading="progressing"
                :disabled="years.length < 1"
                @click="generate"
              >
                generate
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    data () {
      return {
        loading: false,
        progressing: false,
        leaveType: null,
        leaveTypes: [],
        years: [],
        minYear: null,
        maxYear: null,
        startDateMenu: false,
        startDate: this.$moment().startOf('month').format('YYYY-MM-DD'),
        endDateMenu: false,
        endDate: this.$moment().format('YYYY-MM-DD'),        
        progressbar: 0,
      }
    },
    computed: {
      startDateDisplay () {
        return this.startDate ? this.$moment(this.startDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      endDateDisplay () {
        return this.endDate ? this.$moment(this.endDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      maxEndDate () {
        return this.startDate ? this.$moment(this.startDate).endOf('year').format('YYYY-MM-DD') : this.$moment().endOf('year').format('YYYY-MM-DD')
      },   
      minStartDate () {
        return this.$moment([this.minYear]).format('YYYY-MM-DD')
      },
      maxStartDate () {
        return this.$moment([this.maxYear]).endOf('year').format('YYYY-MM-DD')
      },          
    },       
    created () {
      this.loading = true
          
      Promise.all([
        this.$api.leaveTypes().list(),
        this.$api.leaveReports().subordinateLeaveYears(),
      ])
        .then(([leaveTypes, subordinateLeaveYears]) => {
          this.leaveTypes = leaveTypes.data        
          this.years = subordinateLeaveYears.data     
          this.minYear = Math.min(...this.years)
          this.maxYear = Math.max(...this.years)

          this.loading = false
        })
    },
    methods: {
      startDateChange () {
        this.startDateMenu = false
        this.endDate = this.$moment(this.startDate).endOf('month').format('YYYY-MM-DD')
      },        
      generate () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            this.$api.leaveReports().subordinateRemainingLeaveBalance(this.prepareFormData(), {
              onDownloadProgress: (progressEvent) => {
                this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
              },
            })
              .then(res => {
                let year = this.$moment(this.startDate).format('YYYY')

                let blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
                let link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.setAttribute('download', 'Subordinates Leave Summary Reports-' + year + '.xlsx')
                link.click()

                this.progressing = false
                this.loading = false
                this.progressbar = 0

                this.$snack({ message: 'Report downloaded successfully' })
              }).catch(err => {
                this.loading = false
                this.progressing = false

                if (err.response.status === 405) {
                  this.$snack({ color: 'error', message: 'No reporting subordinate.' })
                } else {
                  this.$snack({ color: 'error', message: 'Error! Please contact support' })
                }
              })
          })
      },

      prepareFormData () {
        return {
          leaveTypes: this.leaveType,
          start_date: this.startDate,
          end_date: this.endDate,
          isSubordinatesReport: true,
        }
      },
    },
  }
</script>
